// Drag handle for resizable fixed-width columns in slim list headers.
// Mousedown starts a document-level drag (width deltas reported via
// onResize); double-click resets the column to its default.

import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getColors } from '../theme/tokens';

export interface ColumnResizeHandleProps {
  /** Current column width in px — drag deltas apply from the value at mousedown. */
  width: number;
  onResize: (px: number) => void;
  onReset: () => void;
  /**
   * Edge of the column the handle sits on. Right-anchored columns (e.g. a
   * trailing time column) grow leftward, so their handle sits on the LEFT
   * edge and the drag delta is inverted.
   */
  side?: 'left' | 'right';
}

const ColumnResizeHandle: React.FC<ColumnResizeHandleProps> = ({
  width,
  onResize,
  onReset,
  side = 'right',
}) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  // Holds the teardown for the in-flight drag so it can run on unmount too,
  // not only on mouseup — otherwise listeners and body styles leak if the
  // handle unmounts mid-drag.
  const cleanupRef = useRef<(() => void) | null>(null);
  const flip = side === 'left';

  useEffect(() => () => cleanupRef.current?.(), []);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width };
    const handleMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const delta = ev.clientX - d.startX;
      onResize(d.startWidth + (flip ? -delta : delta));
    };
    const cleanup = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      cleanupRef.current = null;
    };
    const handleUp = () => cleanup();
    cleanupRef.current = cleanup;
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <Box
      onMouseDown={startDrag}
      onDoubleClick={onReset}
      sx={{
        position: 'absolute',
        [side]: -8,
        top: 0,
        bottom: 0,
        width: 12,
        cursor: 'ew-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover > span, &:active > span': { backgroundColor: c.primary },
      }}
    >
      <Box
        component="span"
        sx={{
          width: 3,
          height: 14,
          borderRadius: 1,
          backgroundColor: c.hairline,
          transition: 'background-color 120ms',
        }}
      />
    </Box>
  );
};

export default ColumnResizeHandle;
